import './TableHeader.styles.scss';

import { observer } from 'mobx-react';
import React, { FC, useMemo } from 'react';

import { getPokerRoom } from '../../../constants';
import { Permission } from '../../../model/enumeration/Permission.enum';
import { PokerRoom } from '../../../model/enumeration/PokerRoom';
import authStore from '../../../store/AuthStore';
import permissionStore from '../../../store/PermissionStore';
import playerStore from '../../../store/PlayerStore';
import tableStore from '../../../store/TableStore';
import ButtonWithBotErrors from '../../shared/Button/ButtonWithBotErrors/ButtonWithBotErrors';
import ButtonWithChat from '../../shared/Button/ButtonWithChat/ButtonWithChat';
import { ButtonWithProfits } from '../../shared/Button/ButtonWithProfits/ButtonWithProfits';
import { CloseTableButton } from '../../shared/Button/CloseTableButton/CloseTableButton';
import { LogsButton } from '../../shared/Button/LogsButton/LogsButton';
import { ModalWindowButton } from '../../shared/Button/ModalWindowButton/ModalWindowButton';
import PinTableButton from '../../shared/Button/PinTableButton/PinTableButton';
import { ButtonType } from '../../shared/Button/TableButton.types';
import { TableHistoryButton } from '../../shared/Button/TableHistoryButton/TableHistoryButton';
import WaitingHostButton from '../../shared/Button/WaitingHostButton/WaitingHostButton';
import WaitListButton from '../../shared/Button/WaitListButton/WaitListButton';
import { TableHeaderProps } from './TableHeader.types';
import TableInfo from './TableInfo/TableInfo';

const TableHeader: FC<TableHeaderProps> = ({ table, buttons }) => {
  const hasManageBotSeatingPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS_SEATING),
    [authStore.authenticatedUser],
  );

  const renderButtons = (): React.ReactNode => {
    if (!table) {
      return;
    }
    const { id, deleted, gameType, limit, room } = table;

    return buttons.map(button => {
      switch (button) {
        case ButtonType.PROFITS:
          return (
            <ButtonWithProfits
              key="profits"
              tableId={id}
              tableDeleted={deleted}
            />
          );
        case ButtonType.BOT_ERRORS:
          return <ButtonWithBotErrors key="bot-errors" tableId={id} />;
        case ButtonType.CHAT:
          return <ButtonWithChat key="chat" tableId={id} />;
        case ButtonType.HISTORY:
          return (
            <TableHistoryButton
              key="history"
              tableId={id}
              tableDeleted={deleted}
            />
          );
        case ButtonType.LOGS:
          return <LogsButton key="logs" tableId={id} tableDeleted={deleted} />;
        case ButtonType.PIN_TABLE:
          return <PinTableButton key="pin-table" tableId={id} />;
        case ButtonType.MODAL_WINDOW:
          return (
            <ModalWindowButton
              key="modal"
              tableId={id}
              tableDeleted={deleted}
            />
          );
        case ButtonType.WAITLIST: {
          if (!hasManageBotSeatingPermission) {
            return;
          }
          const union = tableStore.getUnion(id);
          const isTableFull =
            playerStore.findPlayers(table).length === table.players.length;
          return (
            <WaitListButton
              key="wailist"
              tableId={id}
              gameType={gameType}
              limit={limit}
              room={room}
              union={union}
              isTableFull={isTableFull}
              fromLobbyTable={false}
              tablePlayerCount={table.players.length}
              waitlistCount={table.waitlistCount}
            />
          );
        }
        case ButtonType.WAITING_HOST_BOTS: {
          if (getPokerRoom() !== PokerRoom.Pokerrrr2) {
            return;
          }
          return <WaitingHostButton key="waiting-host" table={table} />;
        }
        case ButtonType.CLOSE_TABLE:
          return <CloseTableButton key="close-table" table={table} />;
      }
    });
  };

  return (
    table && (
      <div className="TableHeader-Component">
        <div className="table-info-wrapper">
          <TableInfo table={table} />
        </div>
        {buttons.length > 0 && (
          <div className="buttons-wrapper">{renderButtons()}</div>
        )}
      </div>
    )
  );
};

export default observer(TableHeader);
